import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ticketColumnsV2 } from '../../../tableColumns/hardwareColumns';
import { history } from '../../../utils';
import { IHardware } from '../../../ts/interface/hardware.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import CardView from '../../../components/Redesing/card-view';
import { ITicket } from '../../../ts/interface/problem.interface';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import { MovaticFieldGroupSelectFilter } from '../../../components/Redesing/movatic-field-group';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import useUrlState from '../../../hooks/use-url-state';
import {
  useMaintenanceTicketsRelayQuery,
  MaintenanceTicketSortInput,
  MaintenanceTicketQueryFilter,
} from '../../../hooks/maintenance/query/V2/useMaintenanceTicketsRelay';
import { mapTicketsToLegacy } from '../../../utils/hardwareMappers';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';

const STATUS = [
  { label: 'Canceled', value: 'Canceled' },
  { label: 'Closed', value: 'Closed' },
  { label: 'Assigned', value: 'Assigned' },
  { label: 'Open', value: 'Open' },
];

const VALID_STATUSES = ['open', 'assigned', 'closed', 'canceled'] as const;
type TicketStatus = (typeof VALID_STATUSES)[number];

// Table column id -> GraphQL sort field. Only sortable columns are listed.
const SORT_FIELD_BY_COLUMN: Record<string, MaintenanceTicketSortInput['field']> = {
  due_on: 'DUE_ON',
  status: 'STATUS',
  description: 'DESCRIPTION',
};

const HardwareAssignment = ({ hardware }: { hardware: IHardware }) => {
  const { hardwareId } = useParams<{ hardwareId: string }>();
  const [exportData, setExportData] = useState(false);
  // Keyset (cursor) pagination state. The backend returns opaque keyset cursors,
  // so we feed the previous page's endCursor into `after` and accumulate pages
  // locally, letting PaginationTable slice the requested page.
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [accumulatedTickets, setAccumulatedTickets] = useState<ITicket[]>([]);
  // Cursor pagination can't reconstruct an arbitrary page from a cold URL, so the
  // page index is local state (starts at 0 on load) rather than URL-persisted.
  const [pageNumber, setPageNumber] = useState(0);

  const [filters, setFilters, , isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    selectedStatus: [
      { label: 'Open', value: 'Open' },
      { label: 'Assigned', value: 'Assigned' },
    ] as Option[],
    status: 'Open,Assigned',
    tags: '',
    sortConfig: {
      key: 'due_on',
      direction: 'asc',
    } as ISortConfig,
  });

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Rebuild the chips from the applied, URL-persisted status (the same value
  // that drives the query), so the chips always match the fetched data — after
  // a refresh and when the filter is cleared. Deriving from the applied
  // `filters.status` rather than the default/pending selection keeps chips and
  // query in lock-step. Skip while URL state is still being restored.
  useEffect(() => {
    if (isRestoring) return;
    const next: SearchChip[] = [];
    filters.status
      ?.split(',')
      .filter(Boolean)
      .forEach((value) => {
        const option = STATUS.find((o) => o.value === value);
        next.push({
          label: 'Status',
          field: 'status',
          value,
          displayValue: option?.label ?? value,
          disabled: false,
        });
      });
    setChips(next);
  }, [isRestoring, filters.status]);

  const debouncedSearch = useSearchDebounce(filters.search);

  // Applied status selection -> lowercase status keys for the server filter.
  const statuses = useMemo<TicketStatus[]>(() => {
    if (!filters.status) return [];
    return filters.status
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s): s is TicketStatus =>
        (VALID_STATUSES as readonly string[]).includes(s)
      );
  }, [filters.status]);

  // Server-side filter: scoped to this hardware, plus the applied status set.
  // Fetching, filtering, sorting, searching and pagination all run server-side
  // so results are never capped to a client-held window.
  const ticketFilter = useMemo<MaintenanceTicketQueryFilter>(
    () => ({
      hardwareOldId: hardware?.old_id || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      search: debouncedSearch || undefined,
    }),
    [hardware?.old_id, statuses, debouncedSearch]
  );

  const serverSort = useMemo<MaintenanceTicketSortInput[]>(() => {
    const field = SORT_FIELD_BY_COLUMN[filters.sortConfig?.key ?? ''] ?? 'DUE_ON';
    const order = filters.sortConfig?.direction === 'desc' ? 'DESC' : 'ASC';
    return [{ field, order }];
  }, [filters.sortConfig?.key, filters.sortConfig?.direction]);

  const {
    data: ticketsData,
    count,
    hasMore,
    endCursor,
    isLoading,
    isRefetching,
  } = useMaintenanceTicketsRelayQuery(ticketFilter, {
    limit: filters.sizePerPage,
    sort: serverSort,
    after: afterCursor || undefined,
    enabled: Boolean(hardware?.old_id),
  });

  // Any change to the query inputs (filters, search, sort, page size) invalidates
  // the accumulated cursor pages and returns to the first page.
  const resetPagination = useCallback(() => {
    setAccumulatedTickets([]);
    setAfterCursor(null);
    setPageNumber(0);
  }, []);

  const tickets = useMemo(
    () => mapTicketsToLegacy(ticketsData ?? []) as ITicket[],
    [ticketsData]
  );

  // Accumulate cursor pages so the table can slice the requested page locally.
  // Appends when advancing (afterCursor set); replaces on the first page.
  useMemo(() => {
    if (!tickets.length && !afterCursor) {
      setAccumulatedTickets([]);
      return;
    }
    if (tickets.length) {
      if (afterCursor) {
        setAccumulatedTickets((prev) => [...prev, ...tickets]);
      } else {
        setAccumulatedTickets(tickets);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  const ticketItems = accumulatedTickets.length > 0 ? accumulatedTickets : tickets;

  // Derive the human-readable status shown in the table (incl. "Past Due").
  // Status filtering itself now happens server-side via `ticketFilter`.
  const getStatus = (items: ITicket[]) => {
    const today = new Date().setHours(0, 0, 0, 0);

    return (
      items?.map((ticket) => {
        const dueDate = new Date(ticket.due_on).setHours(0, 0, 0, 0);
        const pastDue = dueDate < today ? ' - Past Due' : '';

        const status = ticket.canceled_on
          ? 'Canceled'
          : ticket.completed_on
          ? 'Closed'
          : ticket.assignee
          ? `Assigned${pastDue}`
          : `Open${pastDue}`;

        return { ...ticket, status };
      }) || []
    );
  };

  const handleChipsChange = useCallback(
    (
      values: string[],
      list: { label: string; value: string }[],
      metadata: { label: string; value: string }
    ): void => {
      resetPagination();
      setFilters((prevState) => ({
        ...prevState,
        sizePerPage: DEFAULT_ROWS_PER_PAGE,
        [metadata.value]: values.join(','),
      }));

      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== metadata.value) {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }

        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = list.find((option) => option.value.toString() === value);
            newChips.push({
              label: metadata.label,
              field: metadata.value,
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    [setFilters, resetPagination]
  );

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });

      resetPagination();
      setFilters((prevState) => ({
        ...prevState,
        sizePerPage: DEFAULT_ROWS_PER_PAGE,
        status: '',
        selectedStatus: [],
      }));
    },
    [setFilters, resetPagination]
  );

  return (
    <>
      <CardView
        headerActions={
          <IconButtonMenu
            buttons={[
              {
                label: 'Export',
                onClick: () => setExportData(true),
                startIcon: <FileDownloadIcon />,
              },
              {
                startIcon: <AddIcon />,
                variant: 'contained',
                label: 'Add',
                onClick: () => history.push(`/maintenance/add/unit/${hardwareId}`),
              },
            ]}
          />
        }
        isLoading={isLoading}
        content={
          <>
            <PaginationTable
              dataId="hardware-tickets"
              customKey={'id'}
              onRowCLick={(id: string) => history.push(`/maintenance/${id}`)}
              exportData={exportData}
              chips={chips}
              handleChipDelete={handleChipDelete}
              csvFileName={'Hardware_Tickets.csv'}
              onExport={() => setExportData(false)}
              onApplyFilter={() => {
                if (
                  JSON.stringify(filters.selectedStatus) !==
                  JSON.stringify(filters.status)
                ) {
                  handleChipsChange(
                    [
                      ...filters.selectedStatus
                        .filter((option) =>
                          STATUS.some((item) => item.label === option.label)
                        )
                        .map((item) => item.value.toString()),
                    ],
                    STATUS,
                    { label: 'Status', value: 'status' }
                  );
                }
              }}
              spaceFilters={
                <>
                  <MovaticFieldGroupSelectFilter
                    dataId="hardware-tickets-status"
                    label={'Status'}
                    value={filters.selectedStatus}
                    onChange={(event: React.SyntheticEvent, newValue: any[]) => {
                      setFilters({
                        ...filters,
                        selectedStatus: newValue,
                      });
                    }}
                    options={STATUS}
                  />
                </>
              }
              sortDirection={filters.sortConfig.direction}
              sortBy={filters.sortConfig.key}
              onSortChange={(sort) => {
                resetPagination();
                setFilters((prevState) => ({
                  ...prevState,
                  sortConfig: sort as ISortConfig,
                }));
              }}
              handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
                resetPagination();
                setFilters((prevState) => ({
                  ...prevState,
                  search: e.target.value,
                }));
              }}
              searchText={filters.search}
              columns={ticketColumnsV2 as IColumn[]}
              items={getStatus(ticketItems)}
              localSearch={false}
              totalItems={count}
              hasMore={hasMore}
              isLoading={
                isRefetching ||
                (pageNumber > 0 &&
                  ticketItems.length <= pageNumber * filters.sizePerPage)
              }
              onPageChange={(page, pageN) => {
                // Advancing past the accumulated pages: fetch the next keyset page.
                if (
                  ticketItems.length &&
                  ticketItems.length / filters.sizePerPage < pageN + 1
                ) {
                  setAfterCursor(endCursor || null);
                }
                setPageNumber(pageN);
              }}
              onRowsPerPageChange={(row) => {
                const pageSize = Number(row.target.value);
                resetPagination();
                setFilters((prevState) => ({
                  ...prevState,
                  sizePerPage: pageSize,
                }));
              }}
              page={pageNumber}
              rowsPerPage={filters.sizePerPage}
              searchPlaceholder={'Search Maintenance Tickets'}
              noDataText={`No maintenance tickets for this ${hardware?.hardware_type_name || 'unit'}`}
            />
          </>
        }
        title={'Maintenance Tickets'}
      />
    </>
  );
};

export default HardwareAssignment;
